import { useTranslation } from 'react-i18next'
import { formatPrice } from '@messenger/utils'
import { CompareSlider } from '@messenger/ui/components/compare-slider'
import { Button } from '@/components/priority/Button/Button'
import { Icon } from '@/components/priority/Icon/Icon'
import type { UseBackgroundRemovalResult } from '@/hooks/editor/useBackgroundRemoval'
import * as S from './BackgroundRemoval.styled'

type BackgroundRemovalPanelProps = {
  /** The photo before any AI alteration, shown while removing and in compares. */
  originalUrl: string
  removal: UseBackgroundRemovalResult
  /** Leave the tool and go back to the normal Pintura editor. */
  onCancel: () => void
  /** Leave the tool with Pintura showing the original photo again. */
  onUseOriginal: () => void
  /** The accepted cutout is on the item; close the whole Adjust Image modal. */
  onDone: () => void
}

/** The panel's test ids; tests import them from here. */
export const TEST_ID = {
  panel: 'bg-removal-panel',
  disclosureConfirm: 'bg-removal-disclosure-confirm',
  disclosureCancel: 'bg-removal-disclosure-cancel',
  startConfirm: 'bg-removal-start-confirm',
  startCancel: 'bg-removal-start-cancel',
  price: 'bg-removal-price',
  priceRetry: 'bg-removal-price-retry',
  removingImage: 'bg-removal-removing-image',
  removingPrice: 'bg-removal-removing-price',
  acceptPrompt: 'bg-removal-accept-prompt',
  compareSlider: 'bg-removal-compare-slider',
  accept: 'bg-removal-accept',
  discard: 'bg-removal-discard',
  applyPrevious: 'bg-removal-apply-previous',
  tryAgain: 'bg-removal-try-again',
  applying: 'bg-removal-applying',
  applyRetry: 'bg-removal-apply-retry',
  cancel: 'bg-removal-cancel',
  done: 'bg-removal-done',
  backToEditing: 'bg-removal-back-to-editing',
  aiTag: 'bg-removal-ai-tag',
  useOriginal: 'bg-removal-use-original',
} as const

/**
 * The screens of the Remove Background tool, rendered over Pintura's stage.
 * Which screen shows is decided entirely by `removal.step` (from
 * useBackgroundRemoval); this component only lays out copy and buttons and
 * forwards clicks to the hook, so every transition stays testable without
 * the DOM.
 *
 * The price is on screen whenever a click can lead to a charge, the way
 * Enhance shows it: on the button that starts a removal (the first-time
 * disclosure and the start screen that replaces it once dismissed), while
 * it runs, and on the accept control, which says the price is added to the
 * order. Neither control works until the backend's price has answered. When
 * the accept found the price changed since the review opened, the accept
 * control says so and names the new amount.
 */
export const BackgroundRemovalPanel = ({
  originalUrl,
  removal,
  onCancel,
  onUseOriginal,
  onDone,
}: BackgroundRemovalPanelProps) => {
  const { t } = useTranslation()
  const {
    step,
    result,
    previous,
    applied,
    appliedPrevious,
    priceCents,
    priceError,
    priceChanged,
    slow,
  } = removal
  // No amount is rendered until the pricing call has answered.
  const price = priceCents === null ? null : formatPrice(priceCents)

  const cancelButton = (
    testId: (typeof TEST_ID)[keyof typeof TEST_ID] = TEST_ID.cancel,
  ) => (
    <Button default onClick={onCancel} data-testid={testId}>
      {t('editor.backgroundRemoval.cancel')}
    </Button>
  )

  const renderStep = () => {
    switch (step) {
      case 'applied':
        return (
          <>
            <S.Stage>
              <S.ResultFrame>
                <img src={applied?.url} alt="" />
                <S.AiTag>
                  <Icon icon="sparkles" fill="#FFFFFF" width="12px" />
                  {t('editor.backgroundRemoval.editedWithAi')}
                </S.AiTag>
              </S.ResultFrame>
            </S.Stage>
            <S.Title>{t('editor.backgroundRemoval.appliedTitle')}</S.Title>
            <S.Body>{t('editor.backgroundRemoval.appliedBody')}</S.Body>
            <S.Actions>
              {cancelButton()}
              <Button
                primary
                onClick={onUseOriginal}
                data-testid={TEST_ID.useOriginal}
              >
                {t('editor.backgroundRemoval.useOriginal')}
              </Button>
            </S.Actions>
          </>
        )

      case 'loading':
        return (
          <>
            <S.Stage>
              <S.Thumbnail src={originalUrl} alt="" />
            </S.Stage>
            <S.Body>{t('editor.backgroundRemoval.checkingPrevious')}</S.Body>
          </>
        )

      case 'previous':
        return (
          <>
            <S.Stage>
              <S.ResultFrame>
                <img src={previous?.url} alt="" />
              </S.ResultFrame>
            </S.Stage>
            <S.Title>{t('editor.backgroundRemoval.previousTitle')}</S.Title>
            <S.Body>{t('editor.backgroundRemoval.previousBody')}</S.Body>
            <S.Actions>
              {cancelButton()}
              <Button
                primary
                onClick={() => void removal.applyPrevious()}
                data-testid={TEST_ID.applyPrevious}
              >
                {t('editor.backgroundRemoval.applyPrevious')}
              </Button>
            </S.Actions>
          </>
        )

      case 'disclosure':
      case 'start': {
        const firstTime = step === 'disclosure'
        return (
          <>
            <S.Stage $heightShare="35%">
              <S.Thumbnail src={originalUrl} alt="" />
            </S.Stage>
            <S.Title>{t('editor.backgroundRemoval.disclosureTitle')}</S.Title>
            {price !== null && (
              <S.Price data-testid={TEST_ID.price}>
                {t('editor.backgroundRemoval.price', { price })}
              </S.Price>
            )}
            {price === null && priceError && (
              <S.Body role="alert">
                {t('editor.backgroundRemoval.priceError')}
              </S.Body>
            )}
            <S.Body>
              {firstTime
                ? t('editor.backgroundRemoval.disclosureBody')
                : t('editor.backgroundRemoval.startBody')}
            </S.Body>
            <S.Actions>
              {cancelButton(
                firstTime ? TEST_ID.disclosureCancel : TEST_ID.startCancel,
              )}
              {price === null && priceError && (
                <Button
                  default
                  onClick={removal.retryPricing}
                  data-testid={TEST_ID.priceRetry}
                >
                  {t('editor.backgroundRemoval.priceRetry')}
                </Button>
              )}
              {/* A paid removal never starts before its price is shown. */}
              <Button
                primary
                onClick={removal.confirmStart}
                disabled={price === null}
                data-testid={
                  firstTime ? TEST_ID.disclosureConfirm : TEST_ID.startConfirm
                }
              >
                {price === null
                  ? t('editor.backgroundRemoval.startButtonNoAmount')
                  : t('editor.backgroundRemoval.startButton', { price })}
              </Button>
            </S.Actions>
          </>
        )
      }

      case 'removing':
        return (
          <>
            <S.Stage>
              <S.RemovingImage
                src={originalUrl}
                alt=""
                data-testid={TEST_ID.removingImage}
              />
            </S.Stage>
            <S.Body role="status" aria-live="polite">
              {slow
                ? t('editor.backgroundRemoval.removingSlow')
                : t('editor.backgroundRemoval.removing')}
            </S.Body>
            {price !== null && (
              <S.Body data-testid={TEST_ID.removingPrice}>
                {t('editor.backgroundRemoval.removingPriceNote', { price })}
              </S.Body>
            )}
            <S.Actions>{cancelButton()}</S.Actions>
          </>
        )

      case 'review':
      case 'accepting': {
        const busy = step === 'accepting'
        // The accept states the price it adds to the order; with none on
        // screen it stays disabled (the hook refuses it too).
        const unpriced = price === null
        return (
          <>
            <S.Stage>
              <CompareSlider
                beforeSrc={originalUrl}
                afterSrc={result?.url ?? ''}
                beforeLabel={t('editor.backgroundRemoval.compareOriginal')}
                afterLabel={t('editor.backgroundRemoval.compareResult')}
                handleLabel={t('editor.backgroundRemoval.compareHandle')}
                data-testid={TEST_ID.compareSlider}
                style={{ width: '100%', height: '100%', borderRadius: 8 }}
              />
              <S.AcceptChip>
                <span data-testid={TEST_ID.acceptPrompt}>
                  {unpriced
                    ? t('editor.backgroundRemoval.acceptPrompt')
                    : priceChanged
                      ? t('editor.backgroundRemoval.acceptPromptPriceChanged', {
                          price,
                        })
                      : t('editor.backgroundRemoval.acceptPromptWithPrice', {
                          price,
                        })}
                </span>
                <S.ChipButton
                  type="button"
                  $accept
                  onClick={() => void removal.accept()}
                  disabled={busy || unpriced}
                  aria-label={
                    unpriced
                      ? t('editor.backgroundRemoval.acceptAria')
                      : t('editor.backgroundRemoval.acceptAriaWithPrice', {
                          price,
                        })
                  }
                  aria-busy={busy}
                  data-testid={TEST_ID.accept}
                >
                  <Icon icon="check" fill="#FFFFFF" width="14px" />
                </S.ChipButton>
                <S.ChipButton
                  type="button"
                  onClick={removal.discard}
                  disabled={busy}
                  aria-label={t('editor.backgroundRemoval.discardAria')}
                  data-testid={TEST_ID.discard}
                >
                  <Icon icon="xMark" fill="#FFFFFF" width="18px" />
                </S.ChipButton>
              </S.AcceptChip>
            </S.Stage>
          </>
        )
      }

      case 'applying':
        return (
          <>
            <S.Stage>
              <S.ResultFrame>
                <img src={result?.url} alt="" />
              </S.ResultFrame>
            </S.Stage>
            <S.Body
              role="status"
              aria-live="polite"
              data-testid={TEST_ID.applying}
            >
              {t('editor.backgroundRemoval.applying')}
            </S.Body>
          </>
        )

      case 'applyFailed':
        return (
          <>
            <S.Stage>
              <S.ResultFrame>
                <img src={result?.url} alt="" />
              </S.ResultFrame>
            </S.Stage>
            <S.Title role="alert">
              {t('editor.backgroundRemoval.applyFailedTitle')}
            </S.Title>
            <S.Body>{t('editor.backgroundRemoval.applyFailedBody')}</S.Body>
            <S.Actions>
              {cancelButton()}
              <Button
                primary
                onClick={() => void removal.retryApply()}
                data-testid={TEST_ID.applyRetry}
              >
                {t('editor.backgroundRemoval.tryAgain')}
              </Button>
            </S.Actions>
          </>
        )

      case 'accepted':
        return (
          <>
            <S.Stage>
              <S.ResultFrame>
                <img src={result?.url} alt="" />
                <S.AiTag data-testid={TEST_ID.aiTag}>
                  <Icon icon="sparkles" fill="#FFFFFF" width="12px" />
                  {t('editor.backgroundRemoval.editedWithAi')}
                </S.AiTag>
                <S.CheckBadge $nudged aria-hidden="true">
                  <Icon icon="check" fill="#FFFFFF" width="14px" />
                </S.CheckBadge>
              </S.ResultFrame>
            </S.Stage>
            <S.Title>{t('editor.backgroundRemoval.acceptedTitle')}</S.Title>
            <S.Body>
              {appliedPrevious
                ? t('editor.backgroundRemoval.acceptedFree')
                : price !== null &&
                  t('editor.backgroundRemoval.acceptedBilled', { price })}
            </S.Body>
            <S.Actions>
              <Button primary onClick={onDone} data-testid={TEST_ID.done}>
                {t('editor.backgroundRemoval.done')}
              </Button>
            </S.Actions>
          </>
        )

      case 'discarded':
        return (
          <>
            <S.Stage>
              <S.Thumbnail src={originalUrl} alt="" />
            </S.Stage>
            <S.Title>{t('editor.backgroundRemoval.discardedTitle')}</S.Title>
            <S.Body>{t('editor.backgroundRemoval.discardedBody')}</S.Body>
            <S.Actions>
              <Button
                primary
                onClick={onCancel}
                data-testid={TEST_ID.backToEditing}
              >
                {t('editor.backgroundRemoval.backToEditing')}
              </Button>
            </S.Actions>
          </>
        )

      case 'error':
        return (
          <>
            <S.Stage>
              <S.Thumbnail src={originalUrl} alt="" />
            </S.Stage>
            <S.Title role="alert">
              {removal.disabled
                ? t('editor.backgroundRemoval.disabledTitle')
                : t('editor.backgroundRemoval.errorTitle')}
            </S.Title>
            <S.Actions>
              {cancelButton()}
              {!removal.disabled && (
                <Button
                  primary
                  onClick={removal.tryAgain}
                  data-testid={TEST_ID.tryAgain}
                >
                  {t('editor.backgroundRemoval.tryAgain')}
                </Button>
              )}
            </S.Actions>
          </>
        )

      default:
        return null
    }
  }

  return (
    <S.Overlay data-testid={TEST_ID.panel} data-step={step}>
      {renderStep()}
    </S.Overlay>
  )
}
